import { FloatingActions } from "@/components/floating-actions/FloatingActions";
import { MdsPanelResponsiveBtn } from "@/components/responsive-btn";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MdsSpacer } from "@/design-system/components/spacer";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { MDSTooltipPlacement } from "@/mds/components/tooltip";
import { MDSButton, MDSButtonSlotName, MDSButtonVariant } from "@/mds";

export interface MultiSelectActionsProps {
  listState: ListStateObservable;
}

export const MultiSelectActions = observer<MultiSelectActionsProps>(({ listState }) => {
  const numberOfSelectedIds = listState.selectedItemIds.length;
  const isOpen = !!numberOfSelectedIds;

  // TODO: consider using 3rd party hooks for all shortcuts
  useEffect(() => {
    const onEscPressed = (event: KeyboardEvent) => {
      if (!listState?.isSelectModeActive) {
        return;
      }

      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      listState.reset();
    };

    document.addEventListener("keydown", onEscPressed);
    return () => {
      document.removeEventListener("keydown", onEscPressed);
    };
  }, [listState]);

  return (
    <FloatingActions isOpen={isOpen}>
      <DeselectButton variant={MDSButtonVariant.EmphasisLight} onClick={listState.clearSelection}>
        <span>{numberOfSelectedIds} selected</span>
        <MdsIcon kind={MdsIconKind.Exit} slot={MDSButtonSlotName.RightIcon} />
      </DeselectButton>
      <Box>
        <MdsSpacer />
        {listState.store.debug.debugModeEnabled && (
          <MdsPanelResponsiveBtn
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Copy"
            collapsedIcon={listState.isCopying ? MdsIconKind.Spinner : MdsIconKind.Copy}
            onClick={listState.handleCopySelectedNotes}
            isDisabled={listState.isCopying}
          />
        )}
        <ChromeActionPanelButton
          threshold="575px"
          variant={MDSButtonVariant.Chrome}
          fullSizeLabel="Auto Organize"
          fullSizeIcon={MdsIconKind.AutoOrganize}
          collapsedIcon={MdsIconKind.AutoOrganize}
          onClick={listState.handleAutoOrganize}
          isDisabled={false}
          collapsedTooltipConfig={{
            label: "Let Mem organize your notes",
            placement: MDSTooltipPlacement.Top,
          }}
          tooltipConfig={{
            label: "Let Mem organize your notes",
            placement: MDSTooltipPlacement.Top,
          }}
        />
        {listState.canChatWithSelection && (
          <ChromeActionPanelButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Chat"
            fullSizeIcon={MdsIconKind.Message}
            collapsedIcon={MdsIconKind.Message}
            onClick={listState.handleChatWithSelection}
            collapsedTooltipConfig={{
              label: "Open a chat with these notes",
              placement: MDSTooltipPlacement.Top,
            }}
            tooltipConfig={{
              label: "Open a chat with these notes",
              placement: MDSTooltipPlacement.Top,
            }}
          />
        )}
        {listState.canAddToCollection && (
          <MdsPanelResponsiveBtn
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="# Organize"
            collapsedIcon={MdsIconKind.Collection}
            onClick={listState.handleAddToCollection}
          />
        )}
        {listState.canRemoveFromCollection && (
          <MdsPanelResponsiveBtn
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Remove from collection"
            collapsedIcon={MdsIconKind.Minus}
            onClick={listState.handleRemoveFromCollection}
          />
        )}
        {listState.canRestoreFromTrash && (
          <MdsPanelResponsiveBtn
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Restore from trash"
            collapsedIcon={MdsIconKind.Redo}
            onClick={listState.handleRestore}
          />
        )}
        {listState.canMoveNoteToTrash && (
          <MdsPanelResponsiveBtn
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            collapsedIcon={MdsIconKind.Trash}
            fullSizeIcon={MdsIconKind.Trash}
            onClick={listState.handleDeleteNote}
            fullSizeLabel="Move to trash"
          />
        )}
        {listState.canMoveCollectionToTrash && (
          <MdsPanelResponsiveBtn
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            collapsedIcon={MdsIconKind.Trash}
            fullSizeIcon={MdsIconKind.Trash}
            onClick={listState.handleDeleteCollections}
            fullSizeLabel="Delete collection"
          />
        )}
      </Box>
    </FloatingActions>
  );
});

const Box = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.surface.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing["3"]};
  font-size: 14px;
`;

const DeselectButton = styled(MDSButton)`
  margin-bottom: ${({ theme }) => theme.spacing.xsPlus};
`;

const ChromeActionPanelButton = styled(MdsPanelResponsiveBtn)`
  & svg {
    font-size: 16px !important;
    height: 16px !important;
    width: 16px !important;
    margin-top: -1px !important;

    color: ${({ theme }) => theme.colors.icon.primary} !important;
  }
`;
